import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Spin, Alert } from "antd";
import { ValidateTVToken } from "../../services/common/cnc_apis";
import * as actionType from "../../services/redux/actionType";
import { getLaborCategories } from "../../services/common/auth_apis";
import { UpdateAppConfig } from "../../services/redux/reducers/app";
import { USER_SITE } from "../../services/common/urls";

function TVDisplayWrapper() {
  const { token } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const authData = useSelector((state) => state.authService);
  
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);

  // Set favicon for TV display pages
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/png';
    link.rel = 'shortcut icon';
    link.href = '/favicon.ico';
    if (!document.querySelector("link[rel*='icon']")) {
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, []);

  // Phase 1: Validate token and set auth
  useEffect(() => {
    if (!token) {
      setError("Token is required");
      setIsValidating(false);
      return;
    }

    ValidateTVToken(token, (result) => {
      if (result && result.status === true && result.data) {
        // Check if token is valid
        if (result.data.valid === true) {
          const userData = result.data.user_data;
          const tokenData = result.data;
          
          // Set authentication state via Redux (same as normal login)
          const authData = {
            user_id: userData.id || userData.user_id,
            username: userData.username || "tv_display",
            customer_id: tokenData.customer_id || userData.customer_id,
            security_level: userData.security_level || "2",
            fullname: userData.username_full || userData.fullname || "TV Display",
            avatar: userData.user_picture || userData.avatar || "",
            time_format: userData.time_format || "",
            is_started_auto: userData.is_started_auto || "",
            start_time: userData.start_time || "0",
            group_ids: userData.group_ids || "",
          };

          // Get labor categories (same as normal login)
          if (authData.user_id) {
            getLaborCategories(authData.user_id, dispatch, () => {});
          }

          dispatch({
            type: actionType.actSetAuthInfo,
            data: authData,
          });

          setTokenInfo(tokenData);
          setIsValidating(false);
        } else {
          // Token is invalid
          const errorMessage = result.data.message || "Invalid or expired token";
          setError(errorMessage);
          setIsValidating(false);
        }
      } else {
        // API call failed or returned error
        const errorMessage = result?.data?.message || result?.message || "Failed to validate token";
        setError(errorMessage);
        setIsValidating(false);
      }
    });
  }, [token, dispatch]);

  // Phase 2: Wait for auth state, then redirect
  useEffect(() => {
    if (!isValidating && tokenInfo && authData.isAuth && authData.customer_id) {
      // Set group_id in Redux app state if machine_group_id is provided
      // This is needed for CNC Pages and Utilization Dashboard to filter machines by group
      if (tokenInfo.machine_group_id && tokenInfo.machine_group_id !== "") {
        UpdateAppConfig({ group_id: tokenInfo.machine_group_id }, dispatch);
      } else {
        // If no group specified, set to "all" to show all machines
        UpdateAppConfig({ group_id: "all" }, dispatch);
      }
      
      // Build target URL based on token info
      const targetUrl = buildDashboardUrl(tokenInfo);
      // Use replace to avoid adding to history
      history.replace(targetUrl);
    }
  }, [isValidating, tokenInfo, authData.isAuth, authData.customer_id, history, dispatch]);

  // Build dashboard URL from token info
  function buildDashboardUrl(info) {
    const { dashboard_type, customer_id, dashboard_id, machine_id } = info;
    const base = USER_SITE;
    
    switch (dashboard_type) {
      case "cnc":
        return `${base}/pages/machine`;
      case "custom_dashboard":
        return `${base}/pages/cw/${customer_id}/${dashboard_id}`;
      case "utilization":
        return `${base}/pages/ud`;
      case "machine_detail":
        // Machine detail from CNC Pages - use query param to auto-select machine
        return `${base}/pages/machine?machine_id=${machine_id}`;
      case "machine_detail_utilization":
        // Machine detail from Utilization Dashboard - use query param
        return `${base}/pages/ud?machine_id=${machine_id}`;
      case "machine_schedule":
        return `${base}/pages/msd/${customer_id}/${machine_id}`;
      case "tms":
        return `${base}/pages/tms`;
      case "tms_alarm":
        return `${base}/pages/tms_alarm`;
      case "sensor":
        return `${base}/pages/iiot`;
      case "maintenance":
        return `${base}/pages/maintenance`;
      case "report":
        return `${base}/pages/report`;
      case "job_entry":
        return `${base}/pages/job_entry`;
      default:
        return `${base}/pages/machine`;
    }
  }

  // Show loading or error
  if (isValidating) {
    return (
      <div style={{ paddingTop: 100, textAlign: "center" }}>
        <Spin tip="Validating token..." size="large" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ paddingTop: 100, textAlign: "center", padding: 20 }}>
        <Alert
          message="Token Error"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  // This should rarely render (redirect happens quickly)
  return (
    <div style={{ paddingTop: 100, textAlign: "center" }}>
      <Spin tip="Redirecting to dashboard..." size="large" />
    </div>
  );
}

export default TVDisplayWrapper;

